export type FlagType =
  | 'wrong_kind'
  | 'wrong_position'
  | 'wrong_template'
  | 'missing_question'
  | 'other';

export interface QuestionComment {
  csvId: number;
  slug: string;
  prompt: string;
  groupLabel: string;
  flagType: FlagType;
  text: string;
  createdAt: string;
}

export const FLAG_LABELS: Record<FlagType, string> = {
  wrong_kind: 'Wrong field type',
  wrong_position: 'Wrong position / order',
  wrong_template: "Shouldn't be on this template",
  missing_question: 'Missing question',
  other: 'Other',
};

// ─── Question-scoped (Questions Browser) ─────────────────────────────────────

export function questionCommentKey(csvId: number) {
  return `question-comment:${csvId}`;
}

export function loadQuestionComment(csvId: number): QuestionComment | null {
  try {
    const raw = localStorage.getItem(questionCommentKey(csvId));
    return raw ? (JSON.parse(raw) as QuestionComment) : null;
  } catch {
    return null;
  }
}

export function saveQuestionComment(comment: QuestionComment) {
  localStorage.setItem(questionCommentKey(comment.csvId), JSON.stringify(comment));
}

export function deleteQuestionComment(csvId: number) {
  localStorage.removeItem(questionCommentKey(csvId));
}

export function loadAllQuestionCommentsFromStorage(): QuestionComment[] {
  const results: QuestionComment[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith('question-comment:')) continue;
      const raw = localStorage.getItem(key);
      if (raw) {
        try { results.push(JSON.parse(raw) as QuestionComment); } catch { /* skip corrupt */ }
      }
    }
  } catch { /* storage unavailable */ }
  return results.sort((a, b) => a.csvId - b.csvId);
}

// ─── Export ───────────────────────────────────────────────────────────────────

export function exportMarkdown(title: string, comments: QuestionComment[]): string {
  const date = new Date().toISOString().slice(0, 10);
  const header = [
    `# ${title}`,
    `**Exported:** ${date}`,
    '',
    '## Issues',
    '',
  ].join('\n');

  const body = comments
    .map((c) =>
      [
        `### Q${c.csvId} — ${c.flagType}`,
        `**Group:** ${c.groupLabel}`,
        `**Prompt:** ${c.prompt}`,
        `**Issue:** ${c.text}`,
        '',
      ].join('\n')
    )
    .join('\n');

  return header + body;
}
